/* eslint-disable */
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { Button, FilterMultiSelect, Modal, Search } from '@pmi/dsm-react';
import Overlay from '../../shared/components/Overlay';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  useAddAccountFavoriteMutation,
  useDeleteAccountFavoriteMutation,
  useGetAccountFavoriteListQuery,
  useGetAccountListV3Query,
  useGetAdminInvitationListQuery,
  useGetAllPointsOfContactsQuery,
  useGetEngagementsQuery,
} from './apiSlice';
import { AccountSelectDto, AdminInvite } from './models';
import AccountSelectTable from './AccountSelectTable';
import {
  buildAdmin2faUrl,
  buildAdmin2faUrlWithAgreement,
  buildAdminInviteUrl,
} from '../../shared/helpers';
import useDebounce from '../../shared/hooks/useDebounce';
import styles from '../../shared/dsm.module.scss';
import accountSelectStyles from './accountselect.module.scss';
import AccountSelectCard from './AccountSelectCard';
import { setCurrentAccount } from '../../shared/store/shared/accountSlice';
import { reportError, toastWithAnalytics } from '../../shared/analytics';
import { setPageTitle, showSidebar } from '../adminLayoutSlice';
import {
  SortedColumn,
  SortDirection,
  AscendingSortDirection,
} from '../../shared/components/ResponsiveTable/models';
import AccountSelectFavoritesTable from './AccountSelectFavoritesTable';
import { useClearAlertMessages } from '../../shared/components/AlertGroup/useAccountAlerts';
import FilterByBlock from '../../shared/components/FilterBy';
import FilterByButton from '../../shared/components/FilterBy/FilterByButton';
import { resetHomeDeal } from '../../shared/store/shared/selectedHomeDealSlice';
import RenderHtml from '../../shared/components/RenderHtml';

function sortFavoriteListTable(
  data: AccountSelectDto[],
  fieldName: string,
  sortOrder: number,
) {
  const clone = [...data];

  return clone?.sort((a, b) =>
    // eslint-disable-next-line no-nested-ternary
    a[fieldName] === b[fieldName]
      ? 0
      : a[fieldName] > b[fieldName]
      ? sortOrder
      : -sortOrder,
  );
}

export default function AccountSelect() {
  useClearAlertMessages();
  const [autoSelectAccount, setAutoSelectAccount] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedInviteId, setSelectedInviteId] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [toRemoveFromFavorites, setToRemoveFromFavorites] = useState(undefined);
  const [sortFavoriteSortedColumn, setSortFavoriteSortedColumn] =
    useState<SortedColumn>(null);
  const [sortAccountsTableDirection, setSortAccountsTableDirection] =
    useState<SortDirection>(null);
  const debouncedSearch = useDebounce(searchInput, 1000);
  const [filterValue, setFilterValue] = useState<string[]>([]);
  const [toggleFilter, setToggleFilter] = useState(false);
  const [selectedPOC, setSelectedPOC] = useState<string[]>([]);
  const [filterPOC, setFilterPOC] = useState<
    { firstName: string; lastName: string }[]
  >([]);
  const {
    data,
    isLoading: accountListLoading,
    isSuccess: success,
    error,
    refetch,
  } = useGetAccountListV3Query({
    page: currentPage,
    pageSize,
    search: debouncedSearch,
    sort: sortAccountsTableDirection,
    engagements: filterValue,
    pointsOfContact: filterPOC,
  });

  const isPowerUser = data?.isPowerUser ?? false;

  const {
    data: rawFaveData,
    isSuccess: faveSuccess,
    error: faveError,
    refetch: faveRefetch,
    isLoading: isLoadingFavs,
  } = useGetAccountFavoriteListQuery({});

  const faveData = useMemo(() => {
    if (isLoadingFavs || !rawFaveData) return rawFaveData?.slice();
    if (sortFavoriteSortedColumn) {
      const sortOrder =
        sortFavoriteSortedColumn.direction === AscendingSortDirection.toString()
          ? 1
          : -1;
      return sortFavoriteListTable(
        rawFaveData,
        sortFavoriteSortedColumn.name,
        sortOrder,
      );
    }
    return sortFavoriteListTable(rawFaveData, 'name', 1);
  }, [rawFaveData, isLoadingFavs, sortFavoriteSortedColumn]);

  const {
    data: invitesData,
    isSuccess: invitesSuccess,
    error: invitesError,
    isLoading: invitesListLoading,
  } = useGetAdminInvitationListQuery(undefined, { skip: isPowerUser });

  const { data: engagementList, isLoading: engagementListLoading } =
    useGetEngagementsQuery({ token: null });

  const { data: pointsOfContactList, isLoading: pointsOfContactListLoading } =
    useGetAllPointsOfContactsQuery({ token: null });

  const [addAccountFavorite, addedAccountFavorite] =
    useAddAccountFavoriteMutation();
  const [deleteAccountFavorite, deletedAccountFavorite] =
    useDeleteAccountFavoriteMutation();

  const accountList = data?.accounts?.items ?? [];
  const favoriteList: any[] = faveData || [];
  const powerUserRoleAssignmentId = data?.powerUserRoleAssignmentId;
  const invitesList = invitesData ?? [];

  const [filterOptions, setFilterOptions] = useState([]);

  useEffect(() => {
    dispatch(resetHomeDeal());
  }, []);

  useEffect(() => {
    dispatch(showSidebar(false));
    dispatch(setPageTitle('Account Selection'));
    dispatch(setCurrentAccount(null));
  }, []);

  useEffect(() => {
    if (engagementList && engagementList.length > 0) {
      const engagements: { label: string; value: string }[] = [];
      engagementList.map((en) => {
        engagements.push({
          label: en,
          value: en,
        });
        return en;
      });
      setFilterOptions(engagements);
    }
  }, [engagementList]);

  /** Non elevated admin handlers */
  useEffect(() => {
    if (
      success &&
      accountList?.length === 1 &&
      invitesSuccess &&
      invitesList?.length === 0 &&
      !isPowerUser
    ) {
      setAutoSelectAccount(true);
      window.location.href = buildAdmin2faUrl(
        accountList[0].accountId,
        accountList[0].roleAssignmentId,
      );
    } else if (
      success &&
      accountList?.length === 0 &&
      invitesSuccess &&
      invitesList?.length === 1 &&
      !isPowerUser
    ) {
      setAutoSelectAccount(true);
      window.location.href = buildAdminInviteUrl(invitesList[0].id);
    }

    if (success && data) {
      const pageCount = Math.ceil(
        Number(data?.accounts?.totalItemCount) / pageSize,
      );
      setTotalPages(pageCount);
    }
  }, [success, accountList, invitesSuccess, invitesList]);

  const pOCListOptions = useMemo(() => {
    if (pointsOfContactList && !pointsOfContactListLoading) {
      const temp = pointsOfContactList.map(
        (c: { firstName: 'string'; lastName: 'string' }) => ({
          label: `${c.firstName} ${c.lastName}`,
          value: `${c.firstName} ${c.lastName}`,
        }),
      );
      return temp?.sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a.label?.toString().toLowerCase() === b.label?.toString().toLowerCase()
          ? 0
          : a.label?.toString().toLowerCase() >
            b.label?.toString().toLowerCase()
          ? 1
          : -1,
      );
    }
    return [];
  }, [pointsOfContactList]);

  const getPOCFirstNameLastName = (
    fullName: string,
  ): { firstName: string; lastName: string } | null => {
    if (pointsOfContactList && pointsOfContactList.length > 0) {
      return pointsOfContactList.find(
        (c) => fullName === `${c.firstName} ${c.lastName}`,
      );
    }
    return null;
  };

  useEffect(() => {
    if (selectedPOC.length > 0) {
      setFilterPOC(selectedPOC.map((f: string) => getPOCFirstNameLastName(f)));
    } else {
      setFilterPOC([]);
    }
  }, [selectedPOC]);

  const handleAccountSelectSubmit = () => {
    const { roleAssignmentId } = accountList.find(
      (a) => a.accountId === selectedAccountId,
    );

    window.location.href = buildAdmin2faUrl(
      selectedAccountId,
      roleAssignmentId,
    );
  };

  const handleInviteSelectSubmit = () => {
    window.location.href = buildAdminInviteUrl(selectedInviteId);
  };

  const handleCancellation = () => {
    navigate('/');
  };

  /** Elevated admin handlers */
  useEffect(() => {
    // refetch with search params
    setCurrentPage(1);
    refetch();
  }, [debouncedSearch, filterValue, filterPOC]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    setTimeout(() => refetch(), 100);
  };

  const onPageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchInput(e.currentTarget.value);
    setCurrentPage(1);
  };

  const onSearchClear = () => {
    setSearchInput('');
  };

  const handleViewAccount = (accountId: string) => () => {
    window.location.href = buildAdmin2faUrl(
      accountId,
      powerUserRoleAssignmentId,
    );
  };
  const handleViewAgreement =
    (accountId: string, agreementId: string) => () => {
      window.location.href = buildAdmin2faUrlWithAgreement(
        accountId,
        agreementId,
        powerUserRoleAssignmentId,
      );
    };
  const handleToFavorites = (item: any) => () => {
    addAccountFavorite(item)
      .unwrap()
      .then(() => {
        refetch();
        faveRefetch();
        const message = (): ReactNode => (
          <>
            <span data-exclude-lg>{item?.name ?? item?.accountName} </span>
            is now added to favorites
          </>
        );
        toastWithAnalytics(message(), 'success');
      })
      .catch(() => {
        const errorMessage = 'Failed to add account to favorites';
        reportError(errorMessage, 1500);
        toastWithAnalytics(errorMessage, 'danger');
      });
  };
  const handleRemoveFromFavorites = (item: any) => () => {
    setToRemoveFromFavorites(item);
  };

  const removeAccountFromFavorites = (item: any) => {
    deleteAccountFavorite({ accountId: item.accountId || item.id })
      .unwrap()
      .then(() => {
        refetch();
        faveRefetch();
        const message = (): ReactNode => (
          <>
            <span data-exclude-lg>{item?.name ?? item?.accountName} </span>
            is now removed from favorites
          </>
        );
        toastWithAnalytics(message(), 'success');
      })
      .catch(() => {
        const errorMessage = 'Failed to remove account from favorites';
        reportError(errorMessage, 1500);
        toastWithAnalytics(errorMessage, 'danger');
      });
  };

  const fetchingError = 'There was an error fetching Account list';
  useEffect(() => {
    if (error || faveError || invitesError) reportError(fetchingError, 1500);
  }, [error, faveError, invitesError]);

  const noAccessError =
    'Your account does not have access to any partner&nbsp;accounts or have any pending&nbsp;invitations.';
  useEffect(() => {
    if (
      success &&
      accountList?.length === 0 &&
      invitesSuccess &&
      invitesList.length === 0 &&
      !isPowerUser
    ) {
      reportError(noAccessError.replaceAll('&nbsp;', ' '), 1500);
    }
  }, [success, accountList, invitesSuccess, invitesList, isPowerUser]);

  useEffect(() => {
    const updateDataExcludeLg = () => {
      const dropdown = document.querySelector('.pocFilter .dropdown-menu');
      const list = document.querySelector('.pocFilter .filter-dropdown-menu');

      if (dropdown && list) {
        if (dropdown.classList.contains('show')) {
          list.setAttribute('data-exclude-lg', 'true');
        } else {
          list.removeAttribute('data-exclude-lg');
        }
      }
    };

    const intervalId = setInterval(updateDataExcludeLg, 100); // Check every 100ms

    // Cleanup the interval on component unmount
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleAccountTableSort = (column: SortedColumn) => {
    setSortAccountsTableDirection(column.direction ?? AscendingSortDirection);
    setTimeout(() => {
      setCurrentPage(1);
    }, 100);
  };

  const onCancelRemoveFromFavorites = () => {
    setToRemoveFromFavorites(undefined);
  };
  const handleFavoriteTableSort = (column: SortedColumn) => {
    setSortFavoriteSortedColumn(column);
  };

  /** Handle different states */
  if (error || invitesError) {
    // Don't show anything if it's 401, let Layout.tsx component handle it.
    const castError: any = error;
    const castInvitesError: any = invitesError;
    if (castError?.status === 401 || castInvitesError?.status === 401)
      return null;

    return (
      <div className="box">
        <h3 className="text-center">{fetchingError}</h3>
      </div>
    );
  }

  if (!success && !faveSuccess && !invitesSuccess) {
    return (
      <div className="p-5">
        <Spinner className="d-block m-auto" />
      </div>
    );
  }

  if (
    success &&
    invitesSuccess &&
    accountList?.length === 0 &&
    invitesList?.length === 0 &&
    !isPowerUser &&
    faveSuccess
  ) {
    return (
      <div className="box">
        <RenderHtml html={noAccessError} tag="h3" className="text-center" />
        <h4 className="text-center">
          Please contact your administrator for further assistance.
        </h4>
      </div>
    );
  }

  if (autoSelectAccount) {
    return (
      <div className="box">
        <h3 className="text-center">Redirecting to your account...</h3>
      </div>
    );
  }

  return (
    <div className="box">
      {!isPowerUser ? (
        <>
          {!accountListLoading && accountList?.length > 0 && (
            <>
              <h3 className="text-center">
                Which administration experience would you like to access?
              </h3>
              <Container>
                <Row className="justify-content-center">
                  {accountList?.map((acc: AccountSelectDto) => (
                    <AccountSelectCard
                      cardDetails={{
                        accountName: acc.accountName,
                        id: acc.accountId,
                        logoUrl: acc.logoUrl,
                      }}
                      handleClick={(accountId) => {
                        setSelectedAccountId(accountId);
                        setSelectedInviteId('');
                      }}
                      key={acc.accountId}
                      selectedId={selectedAccountId}
                    />
                  ))}
                </Row>
                <Row>
                  <Col className="text-center">
                    <Button
                      titleText="Cancel"
                      variant="outline-secondary"
                      onClick={handleCancellation}
                    />
                    <Button
                      disabled={!selectedAccountId}
                      className="m-2"
                      titleText="Select & Proceed"
                      variant="primary"
                      onClick={handleAccountSelectSubmit}
                    />
                  </Col>
                </Row>
              </Container>{' '}
            </>
          )}

          <Container>
            {invitesListLoading && (
              <div className="p-5">
                <Spinner className="d-block m-auto" />
              </div>
            )}
            {!invitesListLoading && invitesList?.length > 0 && (
              <>
                <Row className="justify-content-center pt-4">
                  <h3 className="text-center">Pending Invitations</h3>

                  {invitesList?.map((inv: AdminInvite) => (
                    <AccountSelectCard
                      cardDetails={{
                        accountName: inv.accountName,
                        id: inv.id,
                        logoUrl: inv.accountLogo,
                      }}
                      handleClick={(inviteId) => {
                        setSelectedInviteId(inviteId);
                        setSelectedAccountId('');
                      }}
                      key={inv.id}
                      selectedId={selectedInviteId}
                    />
                  ))}
                </Row>
                <Row>
                  <Col className="text-center">
                    <Button
                      titleText="Cancel"
                      variant="outline-secondary"
                      onClick={handleCancellation}
                    />
                    <Button
                      disabled={!selectedInviteId}
                      className="m-2"
                      titleText="Accept Invitation"
                      variant="primary"
                      onClick={handleInviteSelectSubmit}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Container>
        </>
      ) : (
        <>
          <div className="d-block mb-5">
            <h3>My Favorites</h3>
            <AccountSelectFavoritesTable
              handleViewAccount={handleViewAccount}
              handleViewAgreement={handleViewAgreement}
              handleRemoveFromFavorites={handleRemoveFromFavorites}
              onTableSort={handleFavoriteTableSort}
              sortedColumn={sortFavoriteSortedColumn}
              data={favoriteList}
            />
          </div>
          <div className="d-block">
            <h3>All Accounts</h3>
            <div className="d-flex flex-column flex-sm-row my-3 w-100">
              <div>
                <Search
                  id="userSearch"
                  placeholder="Search by account name or agreement id"
                  value={searchInput}
                  onClear={onSearchClear}
                  onChange={onSearchChange}
                  className={`${styles.secondarySearch} me-0 me-sm-3`}
                />
              </div>
              <div>
                {/* FILTER AND SEARCH */}
                <div className="d-flex w-100">
                  <div
                    className={`d-flex ${accountSelectStyles.filterBySelect}`}
                  >
                    <FilterByButton
                      onClick={() => setToggleFilter(!toggleFilter)}
                      open={toggleFilter}
                    />
                  </div>
                </div>
              </div>
            </div>
            {toggleFilter && (
              <FilterByBlock
                filterElements={[
                  <FilterMultiSelect
                    className={null}
                    label="Engagement Type"
                    options={filterOptions}
                    selected={filterValue}
                    onApply={setFilterValue}
                    isFilterForTable
                    fontSize="md"
                    variant="primary"
                  />,
                  <FilterMultiSelect
                    className={`${accountSelectStyles.pointOfContactFilter} pocFilter`}
                    label="Point of Contact"
                    options={pOCListOptions}
                    selected={selectedPOC}
                    onApply={setSelectedPOC}
                    isFilterForTable
                    fontSize="md"
                    variant="primary"
                    disabled={pointsOfContactListLoading}
                  />,
                ]}
              />
            )}
            <AccountSelectTable
              favorites={favoriteList}
              data={accountList}
              page={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              pageSize={pageSize}
              handleViewAccount={handleViewAccount}
              handleViewAgreement={handleViewAgreement}
              handleToFavorites={handleToFavorites}
              handleRemoveFromFavorites={handleRemoveFromFavorites}
              onTableSort={handleAccountTableSort}
              sortDirection={sortAccountsTableDirection}
            />
          </div>
        </>
      )}
      <Modal
        show={toRemoveFromFavorites ?? false}
        customClassName="m-0"
        variant="dialog"
        onClose={onCancelRemoveFromFavorites}
        title="Remove Account from Favorites"
        buttons={[
          {
            titleText: 'Confirm',
            variant: 'primary',
            onClick: () => {
              removeAccountFromFavorites(toRemoveFromFavorites);
              onCancelRemoveFromFavorites();
            },
          },
          {
            titleText: 'Cancel',
            variant: 'outline-secondary',
            onClick: () => {
              onCancelRemoveFromFavorites();
            },
          },
        ]}
      >
        <div className="p-3">
          <p>
            Remove{' '}
            <strong className="text-primary">
              {toRemoveFromFavorites?.name ??
                toRemoveFromFavorites?.accountName}{' '}
            </strong>
            from favorite accounts?
          </p>
        </div>
      </Modal>
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={
          addedAccountFavorite.isLoading ||
          deletedAccountFavorite.isLoading ||
          engagementListLoading ||
          isLoadingFavs ||
          pointsOfContactListLoading
        }
      />
    </div>
  );
}
